import React from "react";
import MarkdownPreview from "./MarkdownPreview";
import { buildBatches } from "./testCaseMarkdown";

const CHECKLIST_FOOTER =
  "Work through these flexibly. If a step is already done or doesn't apply " +
  "to the current page state, skip it. " +
  "Call `mark_goal_complete` with a summary of what you accomplished for each step. " +
  "Call `mark_goal_failed` only if you cannot make progress on ANY of the remaining steps.";

const VERIFY_HINT =
  "You are a strict QA assertion agent. Check whether the condition described " +
  "below is met on the page, and PASS or FAIL accordingly.";

const CONTEXT_PLACEHOLDER = "(accumulated from results of previous batches at runtime)";

const TYPE_PREFIX = {
  navigate: "Navigate",
  click: "Click",
  type: "Type",
  wait: "Wait",
  text_present: "Text Present",
  element_exists: "Element Exists",
};

/**
 * Builds the full goal string for an action-batch checklist,
 * exactly mirroring _build_checklist_goal in orchestrator.py.
 */
function buildChecklistGoalText(batchItems, hasContext) {
  const parts = [];
  if (hasContext) {
    parts.push(`## Context from previous steps\n${CONTEXT_PLACEHOLDER}`);
  }
  parts.push("Complete the following steps in order. Adapt your approach based on what you see on the page.\n");
  parts.push("## Checklist\n");
  batchItems.forEach(({ item }, j) => {
    const title = item.title || `Step ${j + 1}`;
    parts.push(`### Step ${j + 1}: ${title}\n\n${item.objective || ""}`);
  });
  parts.push("---");
  parts.push(CHECKLIST_FOOTER);
  return parts.join("\n\n");
}

/**
 * Builds the full goal string for a single item (verify, lone action, etc.),
 * exactly mirroring _build_goal in orchestrator.py.
 */
function buildSingleGoalText(objective, hasContext) {
  if (!hasContext) return objective || "";
  return `Context from previous steps:${CONTEXT_PLACEHOLDER}\n\nCurrent goal: ${objective || ""}`;
}

/**
 * Renders a goal string as formatted markdown, showing what the agent receives.
 */
const GoalBlock = ({ text }) => {
  if (!text) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
      <MarkdownPreview markdown={text} />
    </div>
  );
};

const ObjectiveEditor = ({ item, tcIdx, itemIdx, onItemChange }) => (
  <textarea
    value={item.objective || ""}
    onChange={(e) => onItemChange(tcIdx, itemIdx, "objective", e.target.value)}
    placeholder="Objective — what to do or verify"
    rows={Math.max(3, (item.objective || "").split("\n").length + 1)}
    className="w-full bg-gray-50/80 border border-gray-200 rounded-md px-3 py-2 text-xs leading-relaxed hover:border-gray-300 focus:border-violet-400 focus:ring-1 focus:ring-violet-200 focus:outline-none resize-y font-sans"
  />
);

const TitleEditor = ({ value, onChange, className = "" }) => (
  <input
    type="text"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder="Step title"
    className={`flex-1 min-w-0 bg-transparent border-0 border-b border-dashed border-gray-300 hover:border-violet-400 focus:border-violet-500 focus:ring-0 focus:outline-none p-0 text-sm font-semibold leading-tight ${className}`}
  />
);

const ActionBatchSection = ({ batch, tcIdx, batchIndex, onItemChange, readOnly }) => {
  const isMulti = batch.items.length > 1;
  const hasContext = batchIndex > 0;

  if (readOnly) {
    const goalText = isMulti
      ? buildChecklistGoalText(batch.items, hasContext)
      : buildSingleGoalText(batch.items[0].item.objective, hasContext);

    return (
      <div className="rounded-lg border border-blue-200 bg-blue-50/30 px-4 py-3 mt-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700">
            Action Agent
          </span>
          <span className="text-xs text-gray-400">goal string</span>
        </div>
        <GoalBlock text={goalText} />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/30 px-4 py-3 mt-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700">
          Action Agent
        </span>
        {isMulti && (
          <span className="text-xs text-gray-400">{batch.items.length} steps batched</span>
        )}
      </div>

      {hasContext && (
        <p className="text-xs text-gray-400 italic mb-2 border-l-2 border-blue-200 pl-2">
          Context from previous steps will be prepended at runtime
        </p>
      )}

      {isMulti ? (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">
            Complete the following steps in order. Adapt your approach based on what you see on the page.
          </p>
          <p className="text-xs font-semibold text-gray-700">Checklist</p>

          {batch.items.map(({ index: itemIdx, item }, j) => (
            <div key={itemIdx} className="pl-2 border-l-2 border-blue-100">
              <div className="flex items-baseline gap-1.5 mb-1">
                <span className="whitespace-nowrap text-xs font-semibold text-gray-600">Step {j + 1}:</span>
                <TitleEditor
                  value={item.title || ""}
                  onChange={(val) => onItemChange(tcIdx, itemIdx, "title", val)}
                  className="text-gray-900"
                />
              </div>
              <ObjectiveEditor item={item} tcIdx={tcIdx} itemIdx={itemIdx} onItemChange={onItemChange} />
            </div>
          ))}

          <p className="text-xs text-gray-400 italic pt-1 border-t border-gray-100">
            {CHECKLIST_FOOTER}
          </p>
        </div>
      ) : (
        (() => {
          const { index: itemIdx, item } = batch.items[0];
          return (
            <div>
              <div className="flex items-baseline gap-1.5 mb-1">
                <TitleEditor
                  value={item.title || ""}
                  onChange={(val) => onItemChange(tcIdx, itemIdx, "title", val)}
                  className="text-gray-900"
                />
              </div>
              <ObjectiveEditor item={item} tcIdx={tcIdx} itemIdx={itemIdx} onItemChange={onItemChange} />
            </div>
          );
        })()
      )}
    </div>
  );
};

const VerifySection = ({ itemIdx, item, tcIdx, batchIndex, onItemChange, readOnly }) => {
  const hasContext = batchIndex > 0;

  if (readOnly) {
    const goalText = buildSingleGoalText(item.objective, hasContext);

    return (
      <div className="rounded-lg border border-green-200 bg-green-50/30 px-4 py-3 mt-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">
            Verify Agent
          </span>
          <span className="text-xs text-gray-400">goal string</span>
        </div>
        <p className="text-xs text-gray-500 italic mb-2">{VERIFY_HINT}</p>
        <GoalBlock text={goalText} />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-green-200 bg-green-50/30 px-4 py-3 mt-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">
          Verify Agent
        </span>
      </div>
      <p className="text-xs text-gray-500 italic mb-2">{VERIFY_HINT}</p>

      {hasContext && (
        <p className="text-xs text-gray-400 italic mb-2 border-l-2 border-green-200 pl-2">
          Context from previous steps will be prepended at runtime
        </p>
      )}

      <div className="flex items-baseline gap-1.5 mb-1">
        <span className="whitespace-nowrap text-xs font-semibold text-gray-600">Goal:</span>
        <TitleEditor
          value={item.title || ""}
          onChange={(val) => onItemChange(tcIdx, itemIdx, "title", val)}
          className="text-gray-900"
        />
      </div>
      <ObjectiveEditor item={item} tcIdx={tcIdx} itemIdx={itemIdx} onItemChange={onItemChange} />
    </div>
  );
};

const SingleItemSection = ({ itemIdx, item, tcIdx, batchIndex, onItemChange, readOnly }) => {
  const prefix = TYPE_PREFIX[item.item_type] || item.item_type;
  const hasContext = batchIndex > 0;

  if (readOnly) {
    let goalText;
    if (item.item_type === "click") {
      goalText = hasContext
        ? buildSingleGoalText(`Click: ${item.objective}`, hasContext)
        : `Click: ${item.objective || ""}`;
    } else if (item.item_type === "type") {
      goalText = hasContext
        ? buildSingleGoalText(`Type: ${item.objective}`, hasContext)
        : `Type: ${item.objective || ""}`;
    } else if (item.item_type === "wait") {
      goalText = hasContext
        ? buildSingleGoalText(`Wait for: ${item.objective}`, hasContext)
        : `Wait for: ${item.objective || ""}`;
    } else {
      goalText = buildSingleGoalText(item.objective, hasContext);
    }

    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50/30 px-4 py-3 mt-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-600">
            {prefix}
          </span>
          <span className="text-xs text-gray-400">goal string</span>
        </div>
        <GoalBlock text={goalText} />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/30 px-4 py-3 mt-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-600">
          {prefix}
        </span>
      </div>

      {hasContext && (
        <p className="text-xs text-gray-400 italic mb-2 border-l-2 border-gray-300 pl-2">
          Context from previous steps will be prepended at runtime
        </p>
      )}

      <div className="flex items-baseline gap-1.5 mb-1">
        <TitleEditor
          value={item.title || ""}
          onChange={(val) => onItemChange(tcIdx, itemIdx, "title", val)}
          className="text-gray-900"
        />
      </div>
      <ObjectiveEditor item={item} tcIdx={tcIdx} itemIdx={itemIdx} onItemChange={onItemChange} />
    </div>
  );
};

const MarkdownTestCasePreview = ({ testCases, onUpdateTestCase, readOnly }) => {
  if (!testCases || testCases.length === 0) {
    return <p className="text-sm text-gray-400 py-8 text-center">No test cases</p>;
  }

  const handleItemChange = (tcIdx, itemIdx, field, value) => {
    if (readOnly || !onUpdateTestCase) return;
    const tc = testCases[tcIdx];
    const newItems = [...tc.items];
    newItems[itemIdx] = { ...newItems[itemIdx], [field]: value };
    onUpdateTestCase(tcIdx, { ...tc, items: newItems });
  };

  return (
    <div className="space-y-4">
      {testCases.map((tc, tcIdx) => {
        const batches = buildBatches(tc.items || []);

        return (
          <div key={tc.id || tcIdx} className="rounded-lg border border-gray-200 bg-white px-5 py-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-0.5">{tc.name || "Untitled"}</h3>
            <p className="text-xs text-gray-500 mb-0">
              {tc.category && <><strong>Category:</strong> {tc.category} | </>}
              <strong>URL:</strong> {tc.url_placeholder || "root_url"}
              {tc.id && <> | <strong>ID:</strong> <code className="text-xs bg-gray-100 px-1 rounded">{tc.id}</code></>}
              {tc.activated === false && <> | <em className="text-orange-500">disabled</em></>}
            </p>

            {batches.map((batch, bIdx) => {
              if (batch.type === "action_batch") {
                return (
                  <ActionBatchSection
                    key={bIdx}
                    batch={batch}
                    tcIdx={tcIdx}
                    batchIndex={bIdx}
                    onItemChange={handleItemChange}
                    readOnly={readOnly}
                  />
                );
              }

              const { index: itemIdx, item } = batch.items[0];

              if (item.item_type === "verify") {
                return (
                  <VerifySection
                    key={bIdx}
                    itemIdx={itemIdx}
                    item={item}
                    tcIdx={tcIdx}
                    batchIndex={bIdx}
                    onItemChange={handleItemChange}
                    readOnly={readOnly}
                  />
                );
              }

              return (
                <SingleItemSection
                  key={bIdx}
                  itemIdx={itemIdx}
                  item={item}
                  tcIdx={tcIdx}
                  batchIndex={bIdx}
                  onItemChange={handleItemChange}
                  readOnly={readOnly}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default MarkdownTestCasePreview;
